import React, { useContext, useEffect, useState } from 'react';

import { createStyles, makeStyles, Theme, withStyles } from '@material-ui/core/styles';

import ConfirmSelect from '../../components/confirm-select';
import TitleValue from '../../components/titleValue';
import Wait from '../../components/wait';
import { AppContext } from '../../contexts/app-context';
import GlobalStyles from '../../global-styles';
import API from '../../utils/api';
import {
  apiReponseError, apiRequestError, canApprove, canReadCompany, canReadKit, canReadProgram,
  canReadSKU, canWriteKit, canWriteProgram, compareArrayObjectValue, goToRoutePage,
  isNullOrWhiteSpace, setWindowTitle
} from '../../utils/helper';
import {
  AddIcon, CheckCircleIcon, CheckIcon, ClearIcon, CollapseAllBoxIcon, CollapseBoxIcon,
  ErrorOutlineIcon, ExpandAllBoxIcon, ExpandBoxIcon, FormIcon, VisibilityIcon, VisibilityOffIcon,
  WarningIcon
} from '../../utils/icons';
import {
  Alert, AlertTitle, Box, Button, Collapse, Grid, IconButton, Link, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tooltip, Typography
} from '../../utils/material-ui-core';
import ProgramThemeAndMetadataCRUD from './crud/programThemeAndMetadata-curd';

interface OverviewPageAttribute {
  showCompany: boolean;
  validCompany: boolean;
  access2Company: boolean;
  showProgram: boolean;
  validProgram: boolean;
  access2Program: boolean;
  showTheme: boolean;
  validTheme: boolean;
  showKits: boolean;
  validKits: boolean;
  access2Kits: boolean;
  access2SKUs: boolean;
  expandKitItems: boolean;
  lastkeyUsed: string;
}

const displayBooleanIcon = (trueFalse: boolean) => {
  if (trueFalse) {
    return <CheckIcon fontSize="small" color="secondary" />;
  }
  return <ClearIcon fontSize="small" color="disabled" />;
};

const defaultOverviewPageAttribute: OverviewPageAttribute = {
  showCompany: true,
  validCompany: false,
  access2Company: false,
  showProgram: true,
  validProgram: false,
  access2Program: false,
  showTheme: true,
  validTheme: false,
  showKits: true,
  validKits: false,
  access2Kits: false,
  access2SKUs: false,
  expandKitItems: false,
  lastkeyUsed: "",
};

const StyledKitHeaderCell = withStyles((theme) => ({
  head: {
    fontSize: "12px",
    fontWeight: "bold",
    verticalAlign: "top",
    borderBottomColor: "#ffffff",
    color: "#c0c0c0",
  },
}))(TableCell);

const StyledKitItemHeaderCell = withStyles((theme) => ({
  head: {
    color: theme.palette.secondary.main,
    fontSize: "12px",
    fontWeight: "bold",
    verticalAlign: "top",
  },
}))(TableCell);

interface OverviewProps {
  open: boolean;
  refresh: boolean;
  gotoSKUCallBack: any;
}

const Overview = ({ open, refresh, gotoSKUCallBack }: OverviewProps) => {
  const pageStyles = LocalPageStyles();
  const globalStyles = GlobalStyles();

  const { writeLog, setProgram360ProgramKey, programKey, onboardingProgram360Data, refreshData, referenceLookups, userFunctionAccess, appSetting, showAlert } = useContext(AppContext);

  const customOverviewAttributeLocalStorageName = "OverviewAttribute-v2";

  const [themeEditDialogOpen, setThemeEditDialogOpen] = useState<boolean>(false);
  const [loadingProgramView, setLoadingProgramView] = useState<boolean>(true);
  const [selectProgramDialogOpen, setSelecProgramDialogOpen] = useState<boolean>(false);
  const [programLookupValues, setProgramLookupValues] = useState<LookupWithCategory[]>([]);
  const [overviewPageAttribute, setOverviewPageAttribute] = useState<OverviewPageAttribute>({
    ...defaultOverviewPageAttribute,
    access2Company: false,
    access2Program: false,
    access2Kits: false,
    access2SKUs: false,
  });

  const saveReady2Enroll = async () => {
    if (canWriteProgram(userFunctionAccess)) {
      const payload = {};

      try {
        setLoadingProgramView(true);
        await API.post("/api/onboarding/programs/" + programKey + "/readytoenroll", payload)
          .then(async (response) => {
            showAlert({ message: "Successfully saved.", severity: "success" });
            refreshData("programs");
            await refreshData("program360");
            setLoadingProgramView(false);
            return;
          })
          .catch((error) => {
            setLoadingProgramView(false);
            showAlert({
              message: apiReponseError(error, "Failed to save program data."),
              severity: "error",
            });
          });
      } catch (e) {
        setLoadingProgramView(false);
        showAlert({
          message: apiRequestError(e, "Something went wrong in our system."),
          severity: "error",
        });
      }
    }
  };

  const saveThemeAndMetadata = (confirmOutput: ConfirmOutput) => {
    setThemeEditDialogOpen(false);
  };

  const openThemeEdit = () => {
    setThemeEditDialogOpen(true);
  };

  const saveCustomOverviewAttribute = (attribute: OverviewPageAttribute) => {
    localStorage.setItem(customOverviewAttributeLocalStorageName, JSON.stringify(attribute));
  };

  const getCustomOverviewAttribute = () => {
    const tempAttribute = localStorage.getItem(customOverviewAttributeLocalStorageName);
    const tempDefaultAttribute = defaultOverviewPageAttribute as OverviewPageAttribute;

    if (tempAttribute === "undefined" || tempAttribute === undefined || tempAttribute === null || tempAttribute === "") {
      saveCustomOverviewAttribute(tempDefaultAttribute);
    } else {
      const tempAttribute2 = JSON.parse(tempAttribute) as OverviewPageAttribute;
      tempDefaultAttribute.showCompany = tempAttribute2.showCompany;
      tempDefaultAttribute.showProgram = tempAttribute2.showProgram;
      tempDefaultAttribute.showTheme = tempAttribute2.showTheme;
      tempDefaultAttribute.showKits = tempAttribute2.showKits;
      tempDefaultAttribute.expandKitItems = tempAttribute2.expandKitItems;
      setTimeout((lastkeyUsed = tempAttribute2.lastkeyUsed) => {
        scroll2LastkeyUsed(lastkeyUsed);
      }, 500);
    }

    tempDefaultAttribute.access2Company = canReadCompany(userFunctionAccess);
    tempDefaultAttribute.access2Program = canReadProgram(userFunctionAccess);
    tempDefaultAttribute.access2Kits = canReadKit(userFunctionAccess);
    tempDefaultAttribute.access2SKUs = canReadSKU(userFunctionAccess);

    tempDefaultAttribute.validCompany = false;
    tempDefaultAttribute.validProgram = false;
    tempDefaultAttribute.validTheme = false;
    tempDefaultAttribute.validKits = false;

    if (onboardingProgram360Data != null) {
      if (onboardingProgram360Data.validationResults.filter((validationResult) => validationResult.name === "company")?.length === 0) {
        tempDefaultAttribute.validCompany = true;
      }

      if (onboardingProgram360Data.validationResults.filter((validationResult) => validationResult.name === "program")?.length === 0) {
        tempDefaultAttribute.validProgram = true;
      }

      if (onboardingProgram360Data.validationResults.filter((validationResult) => validationResult.name === "theme")?.length === 0) {
        tempDefaultAttribute.validTheme = true;
      }

      if (onboardingProgram360Data.validationResults.filter((validationResult) => validationResult.name === "kits")?.length === 0) {
        tempDefaultAttribute.validKits = true;
      }
    }

    setOverviewPageAttribute(tempDefaultAttribute);
  };

  const scroll2LastkeyUsed = (lastkeyUsed: string) => {
    if (!isNullOrWhiteSpace(lastkeyUsed)) {
      try {
        let lastkeyUsedObject: any = document.getElementById(lastkeyUsed);
        const bottomObject = document.getElementById("_bottom");

        if (lastkeyUsedObject === null) {
          let buttonObject = document.getElementById(lastkeyUsed + "-button");

          const keyObjects = lastkeyUsed.split("-ki-");

          if (keyObjects.length === 2) {
            buttonObject = document.getElementById(keyObjects[0] + "-button");
          }

          if (buttonObject !== null) {
            buttonObject.click();
            lastkeyUsedObject = document.getElementById(lastkeyUsed);
          }
        }

        if (lastkeyUsedObject !== null && bottomObject !== null) {
          bottomObject.scrollIntoView();
          lastkeyUsedObject.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest",
          });

          setTimeout(() => {
            setlastkeyUsed("");
          }, 300);
        }
      } catch (e) {
        setlastkeyUsed("");
      }
    }
  };

  const handleSelectProgramClick = () => {
    const lookupValues: LookupWithCategory[] = [];

    setProgramLookupValues(lookupValues);

    referenceLookups.programs.forEach((program) => {
      let name: any = "";

      name = referenceLookups.clientCompanies.find((clientCompany) => clientCompany.hsbPartnerId === program.hsbPartnerId)?.name;

      if (name === undefined && name === null && name === "") {
        name = "";
      }

      lookupValues.push({
        value: program.programKey,
        name: program.programName,
        category: name,
        checked: false,
      });
    });

    setProgramLookupValues(lookupValues.sort(compareArrayObjectValue("name", "asc")));

    writeLog("Onboarding Page", "Opening program 360 search modal.", "", "");
    setSelecProgramDialogOpen(true);
  };

  const selectedProgram = async (confirmOutput: ConfirmOutput) => {
    setSelecProgramDialogOpen(false);
    if (confirmOutput.status === "select") {
      await setProgram360ProgramKey(confirmOutput.data.value);
    }
  };

  const setShowHideDataContent = (attributeName: string) => {
    let tempAttribute: any;

    if (attributeName === "company") {
      tempAttribute = {
        ...overviewPageAttribute,
        showCompany: !overviewPageAttribute.showCompany,
      };
    } else if (attributeName === "program") {
      tempAttribute = {
        ...overviewPageAttribute,
        showProgram: !overviewPageAttribute.showProgram,
      };
    } else if (attributeName === "theme") {
      tempAttribute = {
        ...overviewPageAttribute,
        showTheme: !overviewPageAttribute.showTheme,
      };
    } else if (attributeName === "kits") {
      tempAttribute = {
        ...overviewPageAttribute,
        showKits: !overviewPageAttribute.showKits,
      };
    }

    tempAttribute.access2Company = canReadCompany(userFunctionAccess);
    tempAttribute.access2Program = canReadProgram(userFunctionAccess);
    tempAttribute.access2Kits = canReadKit(userFunctionAccess);
    tempAttribute.access2SKUs = canReadSKU(userFunctionAccess);

    setOverviewPageAttribute(tempAttribute);
    saveCustomOverviewAttribute(tempAttribute);
  };

  const setShowHideKitItems = () => {
    const tempAttribute = {
      ...overviewPageAttribute,
      expandKitItems: !overviewPageAttribute.expandKitItems,
    };
    tempAttribute.access2Company = canReadCompany(userFunctionAccess);
    tempAttribute.access2Program = canReadProgram(userFunctionAccess);
    tempAttribute.access2Kits = canReadKit(userFunctionAccess);
    tempAttribute.access2SKUs = canReadSKU(userFunctionAccess);

    setOverviewPageAttribute(tempAttribute);
    saveCustomOverviewAttribute(tempAttribute);
  };

  const setlastkeyUsed = (lastkeyUsed: string) => {
    const tempAttribute = {
      ...overviewPageAttribute,
      lastkeyUsed: lastkeyUsed !== "" ? "Key-" + lastkeyUsed : "",
    };
    setOverviewPageAttribute(tempAttribute);
    saveCustomOverviewAttribute(tempAttribute);
  };

  const gotoKit = (kitKey: string, kitItemKey: string, mode: string) => {
    if (canReadKit(userFunctionAccess)) {
      if (canWriteKit(userFunctionAccess)) {
        if (mode === "new") {
          if (kitKey === "") {
            kitKey = referenceLookups.newRecordDefaultId;
            kitItemKey = "";
          } else {
            kitItemKey = referenceLookups.newRecordDefaultId;
          }
        }
      }

      if (kitKey !== "") {
        let path = "onboarding/kits/" + kitKey;

        if (kitItemKey !== "") {
          path = "onboarding/kits/" + kitKey + "/kititems/" + kitItemKey;
        }

        if (kitItemKey !== "" && kitItemKey !== referenceLookups.newRecordDefaultId) {
          setlastkeyUsed(kitKey + "-ki-" + kitItemKey);
        } else if (kitKey !== "" && kitKey !== referenceLookups.newRecordDefaultId) {
          setlastkeyUsed(kitKey);
        } else {
          setlastkeyUsed("");
        }

        goToRoutePage(path);
      }
    }
  };

  const gotoSKU = (sku: string, id: string) => {
    setlastkeyUsed(id);
    gotoSKUCallBack(sku);
  };

  const gotoProgram = () => {
    if (canReadProgram(userFunctionAccess)) {
      const path = "onboarding/programs/" + onboardingProgram360Data.program.programKey;
      goToRoutePage(path);
    }
  };

  const gotoCompany = () => {
    if (canReadCompany(userFunctionAccess)) {
      const path = "onboarding/clientcompanies/" + onboardingProgram360Data.company.clientCompanyKey;
      goToRoutePage(path);
    }
  };

  const getValidationResultItems = (hsbKitNo: number) => {
    let resultItems: ResultItem[] = [];
    const filterValidationResults = onboardingProgram360Data.validationResults.filter((validationResult) => validationResult.name === "kits");
    if (filterValidationResults.length > 0) {
      resultItems = filterValidationResults[0].resultItems.filter((resultItem) => resultItem.type === "e" && resultItem.referenceKey === hsbKitNo.toString());
    }
    return resultItems;
  };

  useEffect(() => {
    setWindowTitle("Onboarding - Program - View");
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const refreshProgram360Data = async () => {
      if (refresh) {
        setSelecProgramDialogOpen(false);
        setLoadingProgramView(true);
        await refreshData("program360");
        setLoadingProgramView(false);
        setTimeout(() => {
          getCustomOverviewAttribute();
        }, 500);
      }
    };

    refreshProgram360Data();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  useEffect(() => {
    if (open) {
      handleSelectProgramClick();
    } else if (programKey === "") {
      handleSelectProgramClick();
    } else {
      writeLog("Onboarding Page", "Opening program 360view.", programKey, "program");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (onboardingProgram360Data.company.clientCompanyKey === "") {
      setLoadingProgramView(false);
    } else {
      setLoadingProgramView(false);
      setTimeout(() => {
        getCustomOverviewAttribute();
      }, 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onboardingProgram360Data]);

  return (
    <React.Fragment>
      <ProgramThemeAndMetadataCRUD open={themeEditDialogOpen} programKey={programKey} onCallback={saveThemeAndMetadata} />

      <ConfirmSelect title={"Select Program"} label={"Kit"} content={``} data={programLookupValues} yesText="SELECT" noText="CANCEL" open={selectProgramDialogOpen} multiSelect={false} maxSelect={1} callbackOnSelect={selectedProgram} />

      {loadingProgramView ? (
        <Wait />
      ) : (
        <Grid container>
          <Grid item xs={12}>
            {onboardingProgram360Data.company.clientCompanyKey === "" ? (
              <Box p={3} display="flex" justifyContent="center">
                <Typography variant="h6">Please select program to view</Typography>
              </Box>
            ) : null}

            {onboardingProgram360Data !== null && onboardingProgram360Data.company.clientCompanyKey !== "" ? (
              <>
                <Box p={0} display="flex" justifyContent="center" alignItems="center" flexDirection="column">
                  <Box>
                    <Typography variant="subtitle1">
                      <b>
                        {onboardingProgram360Data.company.name} - {onboardingProgram360Data.program.programName}
                      </b>
                    </Typography>

                    <Box p={2}></Box>
                  </Box>
                </Box>
                <Box className={pageStyles.reportContainer}>
                  <Box p={0} display="flex" justifyContent="center" alignItems="center" flexDirection="column">
                    {onboardingProgram360Data.program.enrollmentCount > 0 ? (
                      <>
                        <Alert severity="info">{"Please note that limited updates can be made to this program since " + onboardingProgram360Data.program.enrollmentCount + " participants have already been enrolled."}</Alert>
                        <Box p={1}></Box>
                      </>
                    ) : null}
                  </Box>

                  <Grid container spacing={3} className={globalStyles.formikContainer}>
                    <Grid item xs={12}>
                      <Box className={globalStyles.topic}>
                        <Grid container>
                          <Grid item xs={8}>
                            Client Company
                          </Grid>
                          <Grid item xs={4}>
                            <Box display="flex" justifyContent="right">
                              {overviewPageAttribute.access2Company ? (
                                <Tooltip arrow title="View/Edit">
                                  <IconButton>
                                    <Link href="#" className={pageStyles.linkIcon} onClick={() => gotoCompany()}>
                                      <FormIcon fontSize="small" color="secondary" />
                                    </Link>
                                  </IconButton>
                                </Tooltip>
                              ) : null}

                              <Tooltip arrow title={overviewPageAttribute.validCompany ? "No issue found." : "Found some issue in the data."}>
                                <IconButton>
                                  <Link className={pageStyles.linkIcon}>{overviewPageAttribute.validCompany ? <CheckCircleIcon fontSize="small" color="secondary" className={globalStyles.success} /> : <ErrorOutlineIcon fontSize="small" color="secondary" className={globalStyles.error} />}</Link>
                                </IconButton>
                              </Tooltip>

                              <Tooltip arrow title="Show/Hide Company Attributes">
                                <IconButton>
                                  <Link
                                    href="#"
                                    className={pageStyles.linkIcon}
                                    onClick={() => {
                                      setShowHideDataContent("company");
                                    }}
                                  >
                                    {overviewPageAttribute.showCompany ? <VisibilityIcon fontSize="medium" color="secondary" /> : <VisibilityOffIcon fontSize="medium" color="secondary" />}
                                  </Link>
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    </Grid>

                    {overviewPageAttribute.showCompany ? (
                      <Grid item xs={12}>
                        {onboardingProgram360Data.validationResults
                          .filter((validationResult) => validationResult.name === "company")
                          .map((validationResult, index) => (
                            <Box key={index}>
                              <Alert severity="warning">
                                <AlertTitle>Immediate Action Required</AlertTitle>

                                {validationResult.resultItems
                                  .filter((resultItem) => resultItem.type === "e")
                                  .map((resultItem, index) => (
                                    <Box p={1} key={index}>
                                      {resultItem.note}
                                    </Box>
                                  ))}
                              </Alert>
                              <Box p={3}></Box>
                            </Box>
                          ))}

                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={9} md={6} lg={5}>
                            <TitleValue type={"string"} multiline={false} title={"Name"} value={onboardingProgram360Data.company.name} />
                          </Grid>

                          <Grid item xs={12} sm={3} md={3} lg={1}>
                            <TitleValue type={"string"} multiline={false} title={"Active"} value={onboardingProgram360Data.company.active ? "Yes" : "No"} />
                          </Grid>

                          <Grid item xs={12} sm={9} md={6} lg={4}>
                            <TitleValue type={"string"} multiline={false} title={"Parent Name"} value={onboardingProgram360Data.company.parentName} />
                          </Grid>

                          <Grid item xs={12} sm={3} md={3} lg={2}>
                            <TitleValue type={"string"} multiline={false} title={"Parent Code"} value={onboardingProgram360Data.company.hsbPartnerId} />
                          </Grid>

                          <Grid item xs={12} sm={6} md={6} lg={3}>
                            <TitleValue type={"string"} multiline={false} title={"Address Line 1"} value={onboardingProgram360Data.company.address.addressline1} />
                          </Grid>
                          <Grid item xs={12} sm={6} md={6} lg={3}>
                            <TitleValue type={"string"} multiline={false} title={"Address Line 2"} value={onboardingProgram360Data.company.address.addressline2} />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3} lg={2}>
                            <TitleValue type={"string"} multiline={false} title={"City"} value={onboardingProgram360Data.company.address.locality1} />
                          </Grid>
                          <Grid item xs={6} sm={6} md={3} lg={1}>
                            <TitleValue type={"string"} multiline={false} title={"State"} value={onboardingProgram360Data.company.address.region1} />
                          </Grid>
                          <Grid item xs={6} sm={6} md={3} lg={2}>
                            <TitleValue type={"string"} multiline={false} title={"Postal Code"} value={onboardingProgram360Data.company.address.postalCode} />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3} lg={1}>
                            <TitleValue type={"string"} multiline={false} title={"Country"} value={onboardingProgram360Data.company.address.country} />
                          </Grid>

                          <Grid item xs={12}>
                            <Typography variant="h6">Enlistment</Typography>
                          </Grid>

                          <Grid item xs={12} sm={3} md={3} lg={2}>
                            <TitleValue type={"string"} multiline={false} title={"Enable Enlistment"} value={onboardingProgram360Data.company.enableEnlistment ? "Yes" : "No"} />
                          </Grid>
                          <Grid item xs={12} sm={6} md={6} lg={2}>
                            <TitleValue type={"string"} multiline={false} title={"Routing Name"} value={onboardingProgram360Data.company.routingName} />
                          </Grid>
                          <Grid item xs={12} sm={3} md={3} lg={2}>
                            <TitleValue type={"string"} multiline={false} title={"Allow To Change Program"} value={onboardingProgram360Data.company.changeProgram ? "Yes" : "No"} />
                          </Grid>
                          <Grid item xs={12} sm={3} md={3} lg={2}>
                            <TitleValue type={"string"} multiline={false} title={"Manage By AEM"} value={onboardingProgram360Data.company.enlistmentSiteManageByAEM ? "Yes" : "No"} />
                          </Grid>
                          <Grid item xs={12} sm={9} md={9} lg={4}>
                            <TitleValue type={"string"} multiline={false} title={"AEM Site Id"} value={onboardingProgram360Data.company.dcxSiteId} />
                          </Grid>
                        </Grid>
                      </Grid>
                    ) : null}

                    <Grid item xs={12}>
                      <Box className={globalStyles.topic}>
                        <Grid container>
                          <Grid item xs={8}>
                            Program
                          </Grid>
                          <Grid item xs={4}>
                            <Box display="flex" justifyContent="right">
                              {overviewPageAttribute.access2Program ? (
                                <Tooltip arrow title="View/Edit">
                                  <IconButton>
                                    <Link href="#" className={pageStyles.linkIcon} onClick={() => gotoProgram()}>
                                      <FormIcon fontSize="small" color="secondary" />
                                    </Link>
                                  </IconButton>
                                </Tooltip>
                              ) : null}

                              <Tooltip arrow title={overviewPageAttribute.validProgram ? "No issue found." : "Found some issue in the data."}>
                                <IconButton>
                                  <Link className={pageStyles.linkIcon}>{overviewPageAttribute.validProgram ? <CheckCircleIcon fontSize="medium" color="secondary" className={globalStyles.success} /> : <ErrorOutlineIcon fontSize="small" color="secondary" className={globalStyles.error} />}</Link>
                                </IconButton>
                              </Tooltip>

                              <Tooltip arrow title="Show/Hide Program Attributes">
                                <IconButton>
                                  <Link
                                    href="#"
                                    className={pageStyles.linkIcon}
                                    onClick={() => {
                                      setShowHideDataContent("program");
                                    }}
                                  >
                                    {overviewPageAttribute.showProgram ? <VisibilityIcon fontSize="medium" color="secondary" /> : <VisibilityOffIcon fontSize="medium" color="secondary" />}
                                  </Link>
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    </Grid>

                    {overviewPageAttribute.showProgram ? (
                      <Grid item xs={12}>
                        {onboardingProgram360Data.validationResults
                          .filter((validationResult) => validationResult.name === "program")
                          .map((validationResult, index) => (
                            <Box key={index}>
                              <Alert severity="warning">
                                <AlertTitle>Immediate Action Required</AlertTitle>

                                {validationResult.resultItems
                                  .filter((resultItem) => resultItem.type === "e")
                                  .map((resultItem, index) => (
                                    <Box p={1} key={index}>
                                      {resultItem.note}
                                    </Box>
                                  ))}
                              </Alert>
                              <Box p={3}></Box>
                            </Box>
                          ))}

                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={6} md={6}>
                            <TitleValue type={"string"} multiline={false} title={"Program"} value={onboardingProgram360Data.program.programName} />
                          </Grid>
                          <Grid item xs={12} sm={3} md={3}>
                            <TitleValue type={"number"} multiline={false} title={"Program code"} value={onboardingProgram360Data.program.programCode} />
                          </Grid>
                          <Grid item xs={12} sm={3} md={3}>
                            <TitleValue type={"string"} multiline={false} title={"Active"} value={onboardingProgram360Data.program.active ? "Yes" : "No"} />
                          </Grid>

                          <Grid item xs={12} sm={6} md={6} lg={3}>
                            <TitleValue type={"string"} multiline={false} title={"Address Line 1"} value={onboardingProgram360Data.program.address.addressline1} />
                          </Grid>
                          <Grid item xs={12} sm={6} md={6} lg={3}>
                            <TitleValue type={"string"} multiline={false} title={"Address Line 2"} value={onboardingProgram360Data.program.address.addressline2} />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3} lg={2}>
                            <TitleValue type={"string"} multiline={false} title={"City"} value={onboardingProgram360Data.program.address.locality1} />
                          </Grid>
                          <Grid item xs={6} sm={6} md={3} lg={1}>
                            <TitleValue type={"string"} multiline={false} title={"State"} value={onboardingProgram360Data.program.address.region1} />
                          </Grid>
                          <Grid item xs={6} sm={6} md={3} lg={2}>
                            <TitleValue type={"string"} multiline={false} title={"Postal Code"} value={onboardingProgram360Data.program.address.postalCode} />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3} lg={1}>
                            <TitleValue type={"string"} multiline={false} title={"Country"} value={onboardingProgram360Data.program.address.country} />
                          </Grid>

                          <Grid item xs={12}>
                            <Typography variant="h6">Enlistment</Typography>
                          </Grid>

                          <Grid item xs={6} sm={6} md={4} lg={3}>
                            <TitleValue type={"string"} multiline={false} title={"Enable Enlistment"} value={onboardingProgram360Data.program.enableEnlistment ? "Yes" : "No"} />
                          </Grid>

                          <Grid item xs={6} sm={6} md={4} lg={3}>
                            <TitleValue type={"string"} multiline={false} title={"Routing Name"} value={onboardingProgram360Data.program.routingName} />
                          </Grid>

                          <Grid item xs={12} sm={6} md={4} lg={3}>
                            <TitleValue type={"string"} multiline={false} title={"Show For Registration"} value={onboardingProgram360Data.program.showInEnlistmentProductLookup ? "Yes" : "No"} />
                          </Grid>

                          <Grid item xs={12} sm={6} md={4} lg={3}>
                            <TitleValue type={"string"} multiline={false} title={"Is Customer Facing Site"} value={onboardingProgram360Data.program.isEnlistmentCustomerSite ? "Yes" : "No"} />
                          </Grid>

                          <Grid item xs={12} sm={6} md={4} lg={3}>
                            <TitleValue type={"string"} multiline={false} title={"Agreement Confirmation"} value={onboardingProgram360Data.program.showAgreementConfirmation ? "Yes" : "No"} />
                          </Grid>

                          <Grid item xs={6} sm={6} md={4} lg={3}>
                            <TitleValue type={"string"} multiline={false} title={"Enable Subscription"} value={onboardingProgram360Data.program.enableSubscriptionBilling ? "Yes" : "No"} />
                          </Grid>

                          <Grid item xs={6} sm={6} md={4} lg={3}>
                            <TitleValue type={"string"} multiline={false} title={"Enable Auto Enroll"} value={onboardingProgram360Data.program.autoEnroll ? "Yes" : "No"} />
                          </Grid>

                          <Grid item xs={6} sm={6} md={4} lg={3}>
                            <TitleValue type={"string"} multiline={false} title={"Tax Exempt"} value={onboardingProgram360Data.program.taxExempt ? "Yes" : "No"} />
                          </Grid>

                          <Grid item xs={6} sm={6} md={4} lg={3}>
                            <TitleValue type={"string"} multiline={false} title={"Show Kits"} value={onboardingProgram360Data.program.showKits ? "Yes" : "No"} />
                          </Grid>

                          <Grid item xs={12} sm={6} md={4} lg={3}>
                            <TitleValue type={"string"} multiline={false} title={"Group Name"} value={onboardingProgram360Data.program.enlistmentProgramGroupCode} />
                          </Grid>

                          <Grid item xs={12} sm={6} md={4} lg={3}>
                            <TitleValue type={"string"} multiline={false} title={"Max Location Register"} value={onboardingProgram360Data.program.maxAccountCanBeRegister} />
                          </Grid>

                          <Grid item xs={12} sm={6} md={4} lg={3}>
                            <TitleValue type={"string"} multiline={false} title={"Total Pilot Locations"} value={onboardingProgram360Data.program.totalPilotAccountForRegistration} />
                          </Grid>

                          <Grid item xs={12} sm={6} md={4} lg={3}>
                            <TitleValue type={"string"} multiline={false} title={"Meshify Domain"} value={onboardingProgram360Data.program.meshifyDomainName} />
                          </Grid>

                          <Grid item xs={12} sm={6} md={4} lg={3}>
                            <TitleValue type={"string"} multiline={false} title={"Default Package"} value={onboardingProgram360Data.program.defaultPackageId} />
                          </Grid>

                          <Grid item xs={12} sm={6} md={4} lg={3}>
                            <TitleValue type={"string"} multiline={false} title={"Site Manage By AEM"} value={onboardingProgram360Data.program.enlistmentSiteManageByAEM ? "Yes" : "No"} />
                          </Grid>

                          <Grid item xs={12} sm={6} md={8} lg={9}>
                            <TitleValue type={"string"} multiline={false} title={"AEM Site Id"} value={onboardingProgram360Data.program.aemSiteId} />
                          </Grid>

                          <Grid item xs={12}>
                            <Typography variant="h6">External App</Typography>
                          </Grid>

                          <Grid item xs={12} sm={6} md={4} lg={3}>
                            <TitleValue type={"string"} multiline={false} title={"Send To PS Finance"} value={onboardingProgram360Data.program.sendToPSFIN ? "Yes" : "No"} />
                          </Grid>

                          <Grid item xs={12} sm={6} md={4} lg={3}>
                            <TitleValue type={"string"} multiline={false} title={"Shopify Landing Page"} value={onboardingProgram360Data.program.shopifyLandingPage} />
                          </Grid>
                        </Grid>
                      </Grid>
                    ) : null}

                    <Grid item xs={12}>
                      <Box className={globalStyles.topic}>
                        <Grid container>
                          <Grid item xs={8}>
                            Theme / Metadata
                          </Grid>
                          <Grid item xs={4}>
                            <Box display="flex" justifyContent="right">
                              {overviewPageAttribute.access2Program ? (
                                <Tooltip arrow title="View/Edit">
                                  <IconButton>
                                    <Link
                                      href="#"
                                      className={pageStyles.linkIcon}
                                      onClick={() => {
                                        openThemeEdit();
                                      }}
                                    >
                                      <FormIcon fontSize="small" color="secondary" />
                                    </Link>
                                  </IconButton>
                                </Tooltip>
                              ) : null}

                              <Tooltip arrow title={overviewPageAttribute.validTheme ? "No issue found." : "Found some issue in the data."}>
                                <IconButton>
                                  <Link className={pageStyles.linkIcon}>{overviewPageAttribute.validTheme ? <CheckCircleIcon fontSize="medium" color="secondary" className={globalStyles.success} /> : <ErrorOutlineIcon fontSize="small" color="secondary" className={globalStyles.error} />}</Link>
                                </IconButton>
                              </Tooltip>

                              <Tooltip arrow title="Show/Hide Theme Attributes">
                                <IconButton>
                                  <Link
                                    href="#"
                                    className={pageStyles.linkIcon}
                                    onClick={() => {
                                      setShowHideDataContent("theme");
                                    }}
                                  >
                                    {overviewPageAttribute.showTheme ? <VisibilityIcon fontSize="medium" color="secondary" /> : <VisibilityOffIcon fontSize="medium" color="secondary" />}
                                  </Link>
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    </Grid>

                    {overviewPageAttribute.showTheme ? (
                      <Grid item xs={12}>
                        {onboardingProgram360Data.validationResults
                          .filter((validationResult) => validationResult.name === "theme")
                          .map((validationResult, index) => (
                            <Box key={index}>
                              <Alert severity="warning">
                                <AlertTitle>Immediate Action Required</AlertTitle>

                                {validationResult.resultItems
                                  .filter((resultItem) => resultItem.type === "e")
                                  .map((resultItem, index) => (
                                    <Box p={1} key={index}>
                                      {resultItem.note}
                                    </Box>
                                  ))}
                              </Alert>
                              <Box p={3}></Box>
                            </Box>
                          ))}

                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={12} md={4} lg={4}>
                            <TitleValue type={"string"} multiline={false} title={"Company Name"} value={onboardingProgram360Data.programOtherAttribute.theme.companyName} />
                          </Grid>

                          <Grid item xs={12} sm={12} md={4} lg={4}>
                            <TitleValue type={"string"} multiline={false} title={"Client Company Url"} value={onboardingProgram360Data.programOtherAttribute.theme.clientCompanyURL} />
                          </Grid>

                          <Grid item xs={12} sm={6} md={2} lg={2}>
                            <TitleValue type={"string"} multiline={false} title={"Company Path"} value={onboardingProgram360Data.programOtherAttribute.theme.clientCompanyPath} />
                          </Grid>

                          <Grid item xs={12} sm={6} md={2} lg={2}>
                            <TitleValue type={"string"} multiline={false} title={"Program Path"} value={onboardingProgram360Data.programOtherAttribute.theme.programPath} />
                          </Grid>

                          <Grid item xs={12}></Grid>

                          <Grid item xs={12} sm={12} md={4} lg={4}>
                            <TitleValue type={"string"} multiline={false} title={"Company Logo URL"} value={onboardingProgram360Data.programOtherAttribute.theme.logoUrl} />
                          </Grid>

                          <Grid item xs={12} sm={12} md={4} lg={4}>
                            <TitleValue type={"string"} multiline={false} title={"Company Logo Path"} value={onboardingProgram360Data.programOtherAttribute.theme.companyLogo} />
                          </Grid>

                          <Grid item xs={12} sm={12} md={4} lg={4}>
                            <Box display="flex" justifyContent="center" style={{ width: "100%" }}>
                              {appSetting.meshifyUrl !== "" && onboardingProgram360Data.programOtherAttribute.theme.companyLogo !== "" ? <img src={`${appSetting.meshifyUrl}api/files/public${onboardingProgram360Data.programOtherAttribute.theme.companyLogo}`} style={{ width: "60px", height: "auto" }} alt="Favicon" /> : null}
                            </Box>
                          </Grid>

                          <Grid item xs={12} sm={12} md={4} lg={4}>
                            <TitleValue type={"string"} multiline={false} title={"Email URL"} value={onboardingProgram360Data.programOtherAttribute.theme.emailURL} />
                          </Grid>

                          <Grid item xs={12} sm={6} md={4} lg={4}>
                            <TitleValue type={"string"} multiline={false} title={"Phone Number (Enrollment)"} value={onboardingProgram360Data.programOtherAttribute.theme.phoneNumber} />
                          </Grid>
                          <Grid item xs={12} sm={6} md={4} lg={4}>
                            <TitleValue type={"string"} multiline={false} title={"Phone Number (Reactions)"} value={onboardingProgram360Data.programOtherAttribute.theme.supportNumber} />
                          </Grid>

                          <Grid item xs={12}></Grid>

                          <Grid item xs={12} sm={6} md={4} lg={2}>
                            <TitleValue type={"string"} multiline={false} title={"Show Logo"} value={onboardingProgram360Data.programOtherAttribute.theme.showLogo ? "Yes" : "No"} />
                          </Grid>

                          <Grid item xs={12} sm={6} md={4} lg={2}>
                            <TitleValue type={"string"} multiline={false} title={"Show Sensor Store Link"} value={onboardingProgram360Data.programOtherAttribute.theme.sensorStoreLink ? "Yes" : "No"} />
                          </Grid>

                          <Grid item xs={12} sm={6} md={4} lg={2}>
                            <TitleValue type={"string"} multiline={false} title={"Shopify Discount Code"} value={onboardingProgram360Data.programOtherAttribute.theme.shopifyDiscountCode} />
                          </Grid>

                          <Grid item xs={12} sm={6} md={3} lg={2}>
                            <Grid container spacing={3}>
                              <Grid item xs={10} lg={9}>
                                <TitleValue type={"string"} multiline={false} title={"Wave Color"} value={onboardingProgram360Data.programOtherAttribute.theme.waveColor} />
                              </Grid>
                              <Grid item xs={2} lg={3}>
                                <Box p={3} display="flex" justifyContent="center">
                                  <Box
                                    p={1}
                                    display="flex"
                                    justifyContent="center"
                                    style={{
                                      backgroundColor: `${onboardingProgram360Data.programOtherAttribute.theme.waveColor}`,
                                    }}
                                  >
                                    &nbsp;&nbsp;&nbsp;&nbsp;
                                  </Box>
                                </Box>
                              </Grid>
                            </Grid>
                          </Grid>

                          <Grid item xs={12} sm={6} md={3} lg={2}>
                            <TitleValue type={"string"} multiline={false} title={"Wave Opacity"} value={onboardingProgram360Data.programOtherAttribute.theme.waveOpacity} />
                          </Grid>

                          <Grid item xs={12}></Grid>

                          <Grid item xs={12}>
                            <TitleValue type={"string"} multiline={true} title={"Default Schemas"} value={onboardingProgram360Data.programOtherAttribute.theme.defaultSchemas.join(" | ")} />
                          </Grid>

                          <Grid item xs={12}>
                            <TitleValue type={"string"} multiline={true} title={"Device Locations"} value={onboardingProgram360Data.programOtherAttribute.theme.deviceLocations.join(" | ")} />
                          </Grid>

                          <Grid item xs={6}>
                            <TitleValue type={"string"} multiline={false} title={"Participantion Agreement Url"} value={onboardingProgram360Data.programOtherAttribute.theme.participantionAgreementUrl} />
                          </Grid>

                          <Grid item xs={12}>
                            <Typography variant="h6">Enlistment</Typography>
                          </Grid>

                          <Grid item xs={12} sm={6} md={4} lg={2}>
                            <TitleValue type={"string"} multiline={false} title={"Support Phone"} value={onboardingProgram360Data.programOtherAttribute.theme.preEnrollmentSupportEmail} />
                          </Grid>

                          <Grid item xs={12} sm={6} md={4} lg={2}>
                            <TitleValue type={"string"} multiline={false} title={"Support Email"} value={onboardingProgram360Data.programOtherAttribute.theme.preEnrollmentSupportPhone} />
                          </Grid>

                          <Grid item xs={12} sm={6} md={4} lg={6}>
                            <TitleValue type={"string"} multiline={false} title={"Fav Icon"} value={onboardingProgram360Data.programOtherAttribute.theme.enlistmentFavicon} />
                          </Grid>

                          <Grid item xs={12} sm={6} md={4} lg={2}>
                            {appSetting.meshifyUrl !== "" && onboardingProgram360Data.programOtherAttribute.theme.enlistmentFavicon !== "" ? (
                              <Box p={3} display="flex" justifyContent="center" style={{ width: "100%" }}>
                                <img src={`${appSetting.meshifyUrl}api/files/public${onboardingProgram360Data.programOtherAttribute.theme.enlistmentFavicon}`} style={{ width: "60px", height: "auto" }} alt="Favicon" />
                              </Box>
                            ) : null}
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Grid item xs={12}>
                              <TitleValue type={"string"} multiline={false} title={"Hero Banner"} value={onboardingProgram360Data.programOtherAttribute.theme.enlistmentMainPageBackgroundImage} />
                            </Grid>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            {appSetting.meshifyUrl !== "" && onboardingProgram360Data.programOtherAttribute.theme.enlistmentMainPageBackgroundImage !== "" ? (
                              <Box p={3} display="flex" justifyContent="center" style={{ width: "100%" }}>
                                <img src={`${appSetting.meshifyUrl}api/files/public${onboardingProgram360Data.programOtherAttribute.theme.enlistmentMainPageBackgroundImage}`} style={{ width: "100%", height: "auto" }} alt="Hero Banner" />
                              </Box>
                            ) : null}
                          </Grid>

                          <Grid item xs={12}>
                            <Typography variant="h6">Colors</Typography>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Grid container spacing={3}>
                              <Grid item xs={12} sm={12} md={6} lg={6}>
                                <TitleValue type={"string"} multiline={false} title={"Primary Background Color"} value={onboardingProgram360Data.programOtherAttribute.theme.primaryColor} />
                              </Grid>
                              <Grid item xs={12} sm={12} md={6} lg={6}>
                                <TitleValue type={"string"} multiline={false} title={"Primary Text Color"} value={onboardingProgram360Data.programOtherAttribute.theme.primaryTextColor} />
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Box p={3} display="flex" justifyContent="center">
                              <Box
                                p={1}
                                display="flex"
                                justifyContent="center"
                                style={{
                                  backgroundColor: `${onboardingProgram360Data.programOtherAttribute.theme.primaryColor}`,
                                  color: `${onboardingProgram360Data.programOtherAttribute.theme.primaryTextColor}`,
                                }}
                              >
                                Sample 1
                              </Box>
                              <Box
                                p={1}
                                display="flex"
                                justifyContent="center"
                                style={{
                                  backgroundColor: `${onboardingProgram360Data.programOtherAttribute.theme.primaryTextColor}`,
                                  color: `${onboardingProgram360Data.programOtherAttribute.theme.primaryColor}`,
                                }}
                              >
                                Sample 2
                              </Box>
                            </Box>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Grid container spacing={3}>
                              <Grid item xs={12} sm={12} md={6} lg={6}>
                                <TitleValue type={"string"} multiline={false} title={"Secondary Background Color"} value={onboardingProgram360Data.programOtherAttribute.theme.secondaryColor} />
                              </Grid>
                              <Grid item xs={12} sm={12} md={6} lg={6}>
                                <TitleValue type={"string"} multiline={false} title={"Secondary Text Color"} value={onboardingProgram360Data.programOtherAttribute.theme.secondaryTextColor} />
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Box p={3} display="flex" justifyContent="center">
                              <Box
                                p={1}
                                display="flex"
                                justifyContent="center"
                                style={{
                                  backgroundColor: `${onboardingProgram360Data.programOtherAttribute.theme.secondaryColor}`,
                                  color: `${onboardingProgram360Data.programOtherAttribute.theme.secondaryTextColor}`,
                                }}
                              >
                                Sample 1
                              </Box>
                              <Box
                                p={1}
                                display="flex"
                                justifyContent="center"
                                style={{
                                  backgroundColor: `${onboardingProgram360Data.programOtherAttribute.theme.secondaryTextColor}`,
                                  color: `${onboardingProgram360Data.programOtherAttribute.theme.secondaryColor}`,
                                }}
                              >
                                Sample 2
                              </Box>
                            </Box>
                          </Grid>

                          <Grid item xs={12}></Grid>

                          <Grid item xs={12}>
                            <Typography variant="h6">Metadata</Typography>
                          </Grid>

                          <Grid item xs={12} sm={6} md={4}>
                            <TitleValue type={"string"} multiline={false} title={"Pass Shipping Phone To Shopify"} value={onboardingProgram360Data.programOtherAttribute.metadata.passShipToPhoneToShopify ? "Yes" : "No"} />
                          </Grid>

                          <Grid item xs={12} sm={6} md={4}>
                            <TitleValue type={"string"} multiline={false} title={"Subscribe To Installation Alert"} value={onboardingProgram360Data.programOtherAttribute.metadata.subscribeToInstallationAlert ? "Yes" : "No"} />
                          </Grid>

                          <Grid item xs={12} sm={6} md={4}>
                            <TitleValue type={"string"} multiline={false} title={"Whisker Group Type"} value={onboardingProgram360Data.programOtherAttribute.metadata.whiskerGroupType} />
                          </Grid>

                          <Grid item xs={12} sm={6} md={4}>
                            <TitleValue type={"string"} multiline={false} title={"Whisker Group Id"} value={onboardingProgram360Data.programOtherAttribute.metadata.whiskerGroupId} />
                          </Grid>

                          <Grid item xs={12} sm={6} md={4}>
                            <TitleValue
                              type={"string"}
                              multiline={false}
                              title={"Additional Equipment Billing Method"}
                              value={referenceLookups.programBillingMethodTypes
                                .filter((programBillingMethodType) => programBillingMethodType.type === onboardingProgram360Data.programOtherAttribute.metadata.programBillingMethod)
                                .map((programBillingMethodType) => {
                                  return programBillingMethodType.name;
                                })}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6} md={4}>
                            <TitleValue
                              type={"string"}
                              multiline={false}
                              title={"Billable Folder Id"}
                              value={referenceLookups.billableFolderTypes
                                .filter((billableFolderType) => billableFolderType.value === onboardingProgram360Data.programOtherAttribute.metadata.billableFolderId)
                                .map((billableFolderType) => {
                                  return billableFolderType.text;
                                })}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <Box p={1}>&nbsp;</Box>
                          </Grid>
                        </Grid>
                      </Grid>
                    ) : null}

                    <Grid item xs={12}>
                      <Box className={globalStyles.topic}>
                        <Grid container>
                          <Grid item xs={8}>
                            Kits
                          </Grid>
                          <Grid item xs={4}>
                            <Box display="flex" justifyContent="right">
                              {canWriteKit(userFunctionAccess) ? (
                                <Tooltip arrow title="Create new kit and associate to the program">
                                  <IconButton>
                                    <Link
                                      href="#"
                                      className={pageStyles.linkIcon}
                                      onClick={() => {
                                        gotoKit("", "", "new");
                                      }}
                                    >
                                      <AddIcon fontSize="small" color="secondary" />
                                    </Link>
                                  </IconButton>
                                </Tooltip>
                              ) : null}

                              {onboardingProgram360Data.kits.length > 0 ? (
                                <Tooltip arrow title="Show/Hide Kit Items">
                                  <IconButton>
                                    <Link
                                      href="#"
                                      className={pageStyles.linkIcon}
                                      onClick={() => {
                                        setShowHideKitItems();
                                      }}
                                    >
                                      {overviewPageAttribute.expandKitItems ? <CollapseAllBoxIcon color="secondary" /> : <ExpandAllBoxIcon color="secondary" />}
                                    </Link>
                                  </IconButton>
                                </Tooltip>
                              ) : null}

                              {overviewPageAttribute.access2Program ? (
                                <Tooltip arrow title="Go 2 Program to associated kits">
                                  <IconButton>
                                    <Link href="#" className={pageStyles.linkIcon} onClick={() => gotoProgram()}>
                                      <FormIcon fontSize="small" color="secondary" />
                                    </Link>
                                  </IconButton>
                                </Tooltip>
                              ) : null}

                              <Tooltip arrow title={overviewPageAttribute.validKits ? "No issue found." : "Found some issue in the data."}>
                                <IconButton>
                                  <Link className={pageStyles.linkIcon}>{overviewPageAttribute.validKits ? <CheckCircleIcon fontSize="medium" color="secondary" className={globalStyles.success} /> : <ErrorOutlineIcon fontSize="small" color="secondary" className={globalStyles.error} />}</Link>
                                </IconButton>
                              </Tooltip>

                              <Tooltip arrow title="Show/Hide Kits Attributes">
                                <IconButton>
                                  <Link
                                    href="#"
                                    className={pageStyles.linkIcon}
                                    onClick={() => {
                                      setShowHideDataContent("kits");
                                    }}
                                  >
                                    {overviewPageAttribute.showKits ? <VisibilityIcon fontSize="medium" color="secondary" /> : <VisibilityOffIcon fontSize="medium" color="secondary" />}
                                  </Link>
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    </Grid>

                    {overviewPageAttribute.showKits ? (
                      <Grid item xs={12} md={12} lg={12}>
                        {getValidationResultItems(0).length > 0 ? (
                          <>
                            <Alert severity="warning">
                              <AlertTitle>Immediate Action Required</AlertTitle>
                              {getValidationResultItems(0).map((resultItem, index) => (
                                <Box p={1} key={index}>
                                  {resultItem.note}
                                </Box>
                              ))}
                            </Alert>
                            <Box p={3}></Box>
                          </>
                        ) : null}

                        {onboardingProgram360Data.kits.length > 0 ? (
                          <TableContainer>
                            <Table aria-label="Kits" className={pageStyles.customTable}>
                              <TableHead>
                                <TableRow>
                                  <StyledKitHeaderCell className={pageStyles.cell30}>&nbsp;</StyledKitHeaderCell>
                                  <StyledKitHeaderCell className={pageStyles.cell30}>Status</StyledKitHeaderCell>
                                  <StyledKitHeaderCell align="right" className={pageStyles.cell70}>
                                    Kit#
                                  </StyledKitHeaderCell>
                                  <StyledKitHeaderCell className={pageStyles.linkCell}>Name</StyledKitHeaderCell>
                                  <StyledKitHeaderCell align="right" className={pageStyles.cell70}>
                                    Item#
                                  </StyledKitHeaderCell>
                                  <StyledKitHeaderCell align="center" className={pageStyles.cell70}>
                                    Active
                                  </StyledKitHeaderCell>
                                  <StyledKitHeaderCell align="center" className={pageStyles.cell100}>
                                    Replacement Kit
                                  </StyledKitHeaderCell>
                                  <StyledKitHeaderCell align="center" className={pageStyles.cell100}>
                                    Fulfillable
                                  </StyledKitHeaderCell>
                                  <StyledKitHeaderCell align="center" className={pageStyles.cell100}>
                                    Subscription Billing
                                  </StyledKitHeaderCell>
                                  <StyledKitHeaderCell align="right" className={pageStyles.cell100}>
                                    Committment Period
                                  </StyledKitHeaderCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {onboardingProgram360Data.kits.map((kit, index) => (
                                  <DisplayKits key={index} kit={kit} overviewPageAttribute={overviewPageAttribute} expandKitDetail={overviewPageAttribute.expandKitItems} gotoKitCallBack={gotoKit} gotoSKUCallBack={gotoSKU} getValidationResultItemsCallBack={getValidationResultItems} />
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        ) : null}
                      </Grid>
                    ) : null}
                  </Grid>

                  <Box id={"_bottom"}>&nbsp;</Box>
                </Box>

                {canApprove(userFunctionAccess) && canWriteProgram(userFunctionAccess) && !onboardingProgram360Data.program.readyToEnroll ? (
                  <Box p={0} display="flex" justifyContent="center" alignItems="center" flexDirection="column">
                    <Box p={3}>&nbsp;</Box>
                    <Button variant="contained" color="secondary" onClick={saveReady2Enroll}>
                      Ready 2 Enroll
                    </Button>
                    <Box p={3}>&nbsp;</Box>
                  </Box>
                ) : null}
              </>
            ) : (
              <React.Fragment></React.Fragment>
            )}
          </Grid>
        </Grid>
      )}
    </React.Fragment>
  );
};

function DisplayKits(props) {
  const pageStyles = LocalPageStyles();
  const globalStyles = GlobalStyles();

  const { kit, expandKitDetail, gotoKitCallBack, gotoSKUCallBack, getValidationResultItemsCallBack, overviewPageAttribute } = props;

  const { userFunctionAccess } = useContext(AppContext);
  const [open, setOpen] = useState(false);

  overviewPageAttribute.access2Company = canReadCompany(userFunctionAccess);
  overviewPageAttribute.access2Program = canReadProgram(userFunctionAccess);
  overviewPageAttribute.access2Kits = canReadKit(userFunctionAccess);
  overviewPageAttribute.access2SKUs = canReadSKU(userFunctionAccess);

  const isValidationResultItemExists = (hsbKitNo: number) => {
    return getValidationResultItemsCallBack(hsbKitNo).length > 0 ? true : false;
  };

  const getValidationResultItems = (hsbKitNo: number) => {
    return getValidationResultItemsCallBack(hsbKitNo);
  };

  useEffect(() => {
    setOpen(expandKitDetail);
  }, [expandKitDetail]);

  return (
    <React.Fragment>
      <TableRow className={pageStyles.tableRow}>
        <TableCell className={pageStyles.cell30}>
          <Box id={`Key-${kit.kitKey}-button`} onClick={() => setOpen(!open)}>
            <IconButton aria-label="expand row" size="small">
              {open ? <CollapseBoxIcon /> : <ExpandBoxIcon />}
            </IconButton>
          </Box>
        </TableCell>
        <TableCell className={pageStyles.cell30}>{!isValidationResultItemExists(kit.hsbKitNo) ? <CheckCircleIcon fontSize="small" color="secondary" className={globalStyles.success} /> : <ErrorOutlineIcon fontSize="small" color="secondary" className={globalStyles.error} />}</TableCell>
        <TableCell align="right" className={pageStyles.cell70}>
          {kit.hsbKitNo}
        </TableCell>
        <TableCell className={pageStyles.linkCell}>
          {overviewPageAttribute.access2Kits ? (
            <Link
              id={`Key-${kit.kitKey}`}
              className={globalStyles.link}
              onClick={() => {
                gotoKitCallBack(kit.kitKey, "", "edit");
              }}
            >
              {kit.name}
            </Link>
          ) : (
            <>{kit.name}</>
          )}
        </TableCell>
        <TableCell align="right" className={pageStyles.cell70}>
          {kit.kitItems != null ? kit.kitItems.length : 0}
        </TableCell>
        <TableCell align="center" className={pageStyles.cell70}>
          {displayBooleanIcon(kit.active)}
        </TableCell>
        <TableCell align="center" className={pageStyles.cell100}>
          {displayBooleanIcon(kit.replacementKit)}
        </TableCell>
        <TableCell align="center" className={pageStyles.cell100}>
          {displayBooleanIcon(kit.fulfillable)}
        </TableCell>
        <TableCell align="center" className={pageStyles.cell100}>
          {displayBooleanIcon(kit.subscriptionBilling)}
        </TableCell>
        <TableCell align="right" className={pageStyles.cell100}>
          {kit.committmentPeriod}
        </TableCell>
      </TableRow>

      {open ? (
        <TableRow>
          <TableCell />
          <TableCell colSpan={9}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              {isValidationResultItemExists(kit.hsbKitNo) ? (
                <>
                  <Alert severity="warning">
                    <AlertTitle>Immediate Action Required</AlertTitle>
                    {getValidationResultItems(kit.hsbKitNo).map((resultItem, index) => (
                      <Box p={1} key={index}>
                        {resultItem.note}
                      </Box>
                    ))}
                  </Alert>
                  <Box p={3}></Box>
                </>
              ) : null}

              <TableContainer className={pageStyles.kitItemContainer}>
                {kit.kitItems != null ? (
                  <Table className={pageStyles.customTable}>
                    <TableHead>
                      <TableRow>
                        <StyledKitItemHeaderCell>SKU</StyledKitItemHeaderCell>
                        <StyledKitItemHeaderCell>Name</StyledKitItemHeaderCell>
                        <StyledKitItemHeaderCell align="right">Quantity</StyledKitItemHeaderCell>
                        <StyledKitItemHeaderCell align="center">Rollup</StyledKitItemHeaderCell>
                        <StyledKitItemHeaderCell align="center">Recharge Status</StyledKitItemHeaderCell>
                        <StyledKitItemHeaderCell align="right">Variant</StyledKitItemHeaderCell>
                        <StyledKitItemHeaderCell align="right">Subscription</StyledKitItemHeaderCell>
                        <StyledKitItemHeaderCell>Frequency</StyledKitItemHeaderCell>
                        <StyledKitItemHeaderCell>Unit</StyledKitItemHeaderCell>
                        <StyledKitItemHeaderCell align="right">Price</StyledKitItemHeaderCell>
                        <StyledKitItemHeaderCell>Platform Vendor</StyledKitItemHeaderCell>
                        <StyledKitItemHeaderCell>Device Ownership</StyledKitItemHeaderCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {kit.kitItems.map((kitItem, index) => (
                        <TableRow key={index}>
                          <TableCell className={pageStyles.linkCell}>
                            {overviewPageAttribute.access2Kits ? (
                              <Link
                                id={`Key-${kit.kitKey}-ki-${kitItem.kitItemKey}-${kitItem.partNumber}`}
                                className={globalStyles.link}
                                onClick={() => {
                                  gotoSKUCallBack(kitItem.partNumber, `${kit.kitKey}-ki-${kitItem.kitItemKey}-${kitItem.partNumber}`);
                                }}
                              >
                                {kitItem.partNumber}
                              </Link>
                            ) : (
                              <> {kitItem.partNumber}</>
                            )}
                          </TableCell>

                          <TableCell className={pageStyles.linkCell}>
                            {overviewPageAttribute.access2Kits ? (
                              <Link
                                id={`Key-${kit.kitKey}-ki-${kitItem.kitItemKey}`}
                                className={globalStyles.link}
                                onClick={() => {
                                  gotoKitCallBack(kit.kitKey, kitItem.kitItemKey, "edit");
                                }}
                              >
                                {kitItem.name}
                              </Link>
                            ) : (
                              <> {kitItem.name}</>
                            )}
                          </TableCell>
                          <TableCell align="right">{kitItem.quantity}</TableCell>
                          <TableCell align="center">{displayBooleanIcon(kitItem.rollupItem)}</TableCell>
                          <TableCell align="center">
                            {kitItem.rechargeProductStatusDescription === "Ok" ? (
                              <CheckCircleIcon fontSize="small" color="secondary" className={globalStyles.success} />
                            ) : kitItem.rechargeProductStatusDescription === "Not Found" ? (
                              <Tooltip arrow title={"Recharge Product Missing."}>
                                <WarningIcon fontSize="small" color="secondary" className={globalStyles.error} />
                              </Tooltip>
                            ) : null}
                          </TableCell>
                          <TableCell align="right">{kitItem.rechargeProductStatusDescription !== "N/A" ? kitItem.shopifyVariantId : ""}</TableCell>
                          <TableCell align="right">{kitItem.rechargeProductStatusDescription !== "N/A" ? kitItem.subscriptionId : ""}</TableCell>
                          <TableCell>{kitItem.rechargeProductStatusDescription !== "N/A" ? kitItem.intervalFrequency : ""}</TableCell>
                          <TableCell>{kitItem.rechargeProductStatusDescription !== "N/A" ? kitItem.intervalUnit : ""}</TableCell>
                          <TableCell align="right">{kitItem.rechargeProductStatusDescription !== "N/A" ? kitItem.price : ""}</TableCell>

                          <TableCell>{kitItem.platformVendorName}</TableCell>
                          <TableCell>{kitItem.deviceOwnership}</TableCell>
                        </TableRow>
                      ))}

                      <TableRow>
                        <TableCell colSpan={2} align="right" className={pageStyles.footerCell}>
                          <b>Total</b>
                        </TableCell>

                        <TableCell align="right" className={pageStyles.footerCell}>
                          {kit.kitItems.length > 0 ? (
                            <b>
                              {kit.kitItems
                                .map(function (kitItem) {
                                  return kitItem.quantity;
                                })
                                .reduce(function (totalQuantity, quantity) {
                                  return totalQuantity + quantity;
                                })}
                            </b>
                          ) : (
                            <b>0</b>
                          )}
                        </TableCell>

                        <TableCell colSpan={6} className={pageStyles.footerCell}></TableCell>

                        <TableCell align="right" className={pageStyles.footerCell}>
                          {kit.kitItems.length > 0 ? (
                            <b>
                              {kit.kitItems
                                .map(function (kitItem) {
                                  return kitItem.price;
                                })
                                .reduce(function (totalPrice, price) {
                                  return totalPrice + price;
                                })}
                            </b>
                          ) : (
                            <b>0</b>
                          )}
                        </TableCell>
                        <TableCell colSpan={2} className={pageStyles.footerCell}></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                ) : (
                  <></>
                )}
              </TableContainer>
              <Box p={3}></Box>
            </Collapse>
          </TableCell>
        </TableRow>
      ) : null}
    </React.Fragment>
  );
}

const LocalPageStyles = makeStyles((theme: Theme) =>
  createStyles({
    reportContainer: {
      // height: 'calc(100vh - 550px)',
      // overflow: 'auto',
      // overflowX: 'hidden',
      width: "100%",
      paddingBottom: "10px",
      [theme.breakpoints.down("xs")]: {
        height: "calc(100vh - 450px)",
      },
    },
    tableRow: {
      "& .MuiTableCell-root": {
        verticalAlign: "top",
      },
      "& > *": {
        borderBottom: "unset",
      },
    },
    linkIcon: {
      // paddingLeft: 8,
      // paddingRight: 8,
      marginLeft: 10
    },
    kitItemContainer: {
      "& .MuiTableHead-root": {
        backgroundColor: theme.palette.type === "dark" ? theme.palette.grey[800] : theme.palette.grey[200],
      },
    },
    customTable: {
      //minWidth: 650,
    },
    linkCell: {
      minWidth: "200px",
    },
    cell100: {
      width: "110px",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    cell70: {
      width: "70px",
      overflow: "hidden",
    },
    cell30: {
      width: "30px",
      overflow: "hidden",
    },
    footerCell: {
      backgroundColor: theme.palette.type === "dark" ? theme.palette.grey[800] : theme.palette.grey[200],
    },
  })
);

export default Overview;
